import {
  CanonicalDataType,
  isLatitudeType,
  isLongitudeType,
  LatitudeDataType,
  LongitudeDataType,
  MessagePathPart,
  MessagePathPartType,
} from "@/shared/domain/topics";
import { type Message } from "@/shared/reader";
import { MapPath, TopicData } from "@/shared/state/visualization/schema/v1";

import { valueAtMessagePath } from "../../messagePath";

export interface TimestampedLocation {
  latitude: number;
  longitude: number;
  timestamp: number;
}

export interface GeoMessagePath {
  messagePath: MessagePathPart[];
  canonicalDataType: LatitudeDataType | LongitudeDataType;
}

export interface PathData {
  latMessagePath: GeoMessagePath;
  lonMessagePath: GeoMessagePath;
  messages: Message<Record<string, unknown>>[];
}

export function getNormalizedLatLon(
  message: Message<Record<string, unknown>>,
  latMessagePath: GeoMessagePath,
  lonMessagePath: GeoMessagePath,
): [number, number] | null {
  const lat = normalizeCoordinate(
    valueAtMessagePath(message.data, latMessagePath.messagePath),
    latMessagePath.canonicalDataType,
  );
  const lon = normalizeCoordinate(
    valueAtMessagePath(message.data, lonMessagePath.messagePath),
    lonMessagePath.canonicalDataType,
  );

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return [lat as number, lon as number];
}

function normalizeCoordinate(
  value: unknown,
  dataType: CanonicalDataType,
): number | undefined {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return undefined;
  }

  switch (dataType) {
    case CanonicalDataType.Number:
    case CanonicalDataType.LatDegFloat:
    case CanonicalDataType.LonDegFloat:
      return value;
    case CanonicalDataType.LatDegInt:
    case CanonicalDataType.LonDegInt:
      return value / 1e7;
    default:
      throw new Error("Unsupported coordinate data type");
  }
}

function getTopicDataType(topicData: TopicData): CanonicalDataType | undefined {
  const finalMsgPathPart = [...topicData.messagePath.parts].pop();
  if (finalMsgPathPart && finalMsgPathPart.type === MessagePathPartType.Attr) {
    return finalMsgPathPart.dataType;
  }
}

function getTopicDataMsgPath(topicData: TopicData): MessagePathPart[] {
  return topicData.messagePath.parts;
}

/**
 * Extract latitude and longitude message paths from the path data.
 */
export function getGeoMessagePaths(path: MapPath): {
  latMessagePath: GeoMessagePath;
  lonMessagePath: GeoMessagePath;
} {
  const [dataA, dataB] = path.data;
  const msgPathA = getTopicDataMsgPath(dataA);
  const dataTypeA = getTopicDataType(dataA);
  const msgPathB = getTopicDataMsgPath(dataB);
  const dataTypeB = getTopicDataType(dataB);

  if (!msgPathA || !dataTypeA || !msgPathB || !dataTypeB) {
    throw new Error("Missing required geographic data");
  }

  if (!isLatitudeType(dataTypeA)) {
    throw new Error(`Expected latitude type for data[0], got ${dataTypeA}`);
  }

  if (!isLongitudeType(dataTypeB)) {
    throw new Error(`Expected longitude type for data[1], got ${dataTypeB}`);
  }

  const latMessagePath: GeoMessagePath = {
    messagePath: msgPathA,
    canonicalDataType: dataTypeA,
  };

  const lonMessagePath: GeoMessagePath = {
    messagePath: msgPathB,
    canonicalDataType: dataTypeB,
  };

  return { latMessagePath, lonMessagePath };
}
